/**
 * Money crosses the wire as a decimal STRING — "28.80", never 28.8.
 *
 * The backend holds paise as integers precisely so nothing rounds in transit;
 * parsing to a float on the way in would undo that. So the string is carried
 * around untouched and parsed exactly once, at the moment it is drawn.
 *
 * Nothing in this app adds money together, and nothing should: the one figure
 * a rider acts on is the COD amount on a single order, quoted by the server.
 * A client that summed its own numbers would eventually disagree with the
 * bill, and the rider would meet that disagreement at a doorway with cash in
 * their hand.
 */
export type Money = string;

const rupees = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatMoney(value: Money | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number.parseFloat(value);
  return Number.isNaN(amount) ? value : rupees.format(amount);
}

export function isZeroMoney(value: Money | null | undefined): boolean {
  return (Number.parseFloat(value ?? '0') || 0) === 0;
}

/** "4:05 pm" — the resolution a run sheet is read at. */
export function formatTime(value: Date | string | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit' }).format(date);
}

/** "12 Mar, 4:05 pm" */
export function formatDateTime(value: Date | string | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * "Today" / "Yesterday" / "12 Mar" — how a shift is actually remembered.
 *
 * History is grouped by this rather than by date, because a rider looking back
 * is nearly always looking for something that happened in the last two days.
 */
export function formatDayLabel(value: Date | string | null | undefined): string {
  const date = toDate(value);
  if (!date) return 'Earlier';

  const startOfDay = (input: Date) =>
    new Date(input.getFullYear(), input.getMonth(), input.getDate()).getTime();

  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' }).format(date);
}

/**
 * How long a job has been open, in the words a rider would use.
 *
 * Rounded hard and deliberately: the number is a sense of urgency, not a
 * measurement, and "1h 47m" invites arithmetic nobody standing on a pavement
 * wants to do.
 */
export function formatElapsed(value: Date | string | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';

  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** "1 job" / "3 jobs", without the caller assembling it at every call site. */
export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * The two letters on the avatar. "·" rather than an empty circle for someone
 * whose name the platform has not filled in yet.
 */
export function initials(name: string | null | undefined): string {
  const parts = (name ?? '')
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
